/**
 * Print utilities for generating accessible PDFs from Analysis Reports
 * Adapted from legislative-review's print system
 */

/**
 * Generate HTML template for printing analysis report
 */
export function generatePrintHTML(title: string, content: string): string {
  return `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${title}</title>
        <style>
          /* Base styles */
          * {
            box-sizing: border-box;
          }
          
          body {
            font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
            margin: 0;
            padding: 2rem;
            color: #111827;
            background: white;
            line-height: 1.6;
            font-size: 11pt;
          }
          
          /* Typography */
          h1, h2, h3, h4, h5, h6 {
            color: #111827;
            font-weight: 700;
            margin-top: 1.5rem;
            margin-bottom: 0.75rem;
          }
          
          h1 {
            font-size: 24pt;
            border-bottom: 2px solid #E21833;
            padding-bottom: 0.5rem;
            margin-bottom: 1.5rem;
          }
          
          h2 {
            font-size: 18pt;
            border-bottom: 1px solid #e5e7eb;
            padding-bottom: 0.5rem;
          }
          
          h3 {
            font-size: 14pt;
          }
          
          h4 {
            font-size: 12pt;
          }
          
          p {
            margin: 0.5rem 0;
          }
          
          /* Spacing utilities */
          .space-y-8 > * + * { margin-top: 2rem; }
          .space-y-6 > * + * { margin-top: 1.5rem; }
          .space-y-4 > * + * { margin-top: 1rem; }
          .space-y-3 > * + * { margin-top: 0.75rem; }
          .space-y-2 > * + * { margin-top: 0.5rem; }
          
          /* Card/Section styles */
          .bg-white {
            background: white;
            padding: 1.5rem;
            border-radius: 0.75rem;
            border: 1px solid #e5e7eb;
            margin-bottom: 1.5rem;
          }
          
          .bg-gray-50 {
            background: #f9fafb;
            border: 1px solid #e5e7eb;
            padding: 1rem;
            border-radius: 0.5rem;
          }
          
          .shadow-lg {
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          }
          
          /* Grid layouts */
          .grid {
            display: grid;
            gap: 1rem;
          }
          
          .grid-cols-2 {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .grid-cols-3 {
            grid-template-columns: repeat(3, 1fr);
          }
          
          /* Badge styles - WCAG AA compliant */
          .badge {
            display: inline-block;
            padding: 0.25rem 0.75rem;
            border-radius: 0.375rem;
            font-size: 9pt;
            font-weight: 600;
            border: 1px solid;
          }
          
          .bg-red-100 {
            background: #fee2e2;
            color: #7f1d1d;
            border-color: #dc2626;
          }
          
          .bg-orange-100 {
            background: #fed7aa;
            color: #7c2d12;
            border-color: #ea580c;
          }
          
          .bg-yellow-100 {
            background: #fef08a;
            color: #713f12;
            border-color: #ca8a04;
          }
          
          .bg-green-100 {
            background: #d1fae5;
            color: #064e3b;
            border-color: #059669;
          }
          
          .bg-blue-100 {
            background: #dbeafe;
            color: #1e3a8a;
            border-color: #2563eb;
          }
          
          .bg-gray-100 {
            background: #f3f4f6;
            color: #1f2937;
            border-color: #6b7280;
          }
          
          .text-\\[\\#E21833\\] {
            color: #E21833;
          }
          
          .bg-\\[\\#E21833\\] {
            background: #E21833;
            color: white;
          }
          
          /* Lists */
          ul, ol {
            padding-left: 1.5rem;
            margin: 0.5rem 0;
          }
          
          li {
            margin-bottom: 0.25rem;
          }
          
          /* Text colors */
          .text-gray-600 {
            color: #6b7280;
          }
          
          .text-gray-700 {
            color: #374151;
          }
          
          .text-gray-800 {
            color: #1f2937;
          }
          
          .text-gray-900 {
            color: #111827;
          }
          
          /* Flex utilities */
          .flex {
            display: flex;
          }
          
          .items-center {
            align-items: center;
          }
          
          .justify-between {
            justify-content: space-between;
          }
          
          .gap-2 {
            gap: 0.5rem;
          }
          
          .gap-3 {
            gap: 0.75rem;
          }
          
          .gap-4 {
            gap: 1rem;
          }
          
          /* Font weights */
          .font-bold {
            font-weight: 700;
          }
          
          .font-semibold {
            font-weight: 600;
          }
          
          .font-medium {
            font-weight: 500;
          }
          
          /* Text sizes */
          .text-sm {
            font-size: 10pt;
          }
          
          .text-xs {
            font-size: 9pt;
          }
          
          .text-\\[9px\\] {
            font-size: 9px;
          }
          
          .text-\\[10px\\] {
            font-size: 10px;
          }
          
          .text-\\[8px\\] {
            font-size: 8px;
          }
          
          /* Flex utilities */
          .flex-1 {
            flex: 1 1 0%;
          }
          
          .flex-shrink-0 {
            flex-shrink: 0;
          }
          
          /* Width utilities */
          .w-32 {
            width: 8rem;
          }
          
          .w-40 {
            width: 10rem;
          }
          
          .w-48 {
            width: 12rem;
          }
          
          /* Min-width utilities */
          .min-w-\\[35px\\] {
            min-width: 35px;
          }
          
          .min-w-\\[45px\\] {
            min-width: 45px;
          }
          
          .min-w-\\[60px\\] {
            min-width: 60px;
          }
          
          /* Height utilities */
          .h-6 {
            height: 1.5rem;
          }
          
          .h-7 {
            height: 1.75rem;
          }
          
          .h-8 {
            height: 2rem;
          }
          
          /* Gap utilities */
          .gap-0\\.5,
          .gap-0 {
            gap: 0.125rem;
          }
          
          /* Border radius */
          .rounded {
            border-radius: 0.25rem;
          }
          
          .rounded-xl {
            border-radius: 0.75rem;
          }
          
          /* Position utilities */
          .relative {
            position: relative;
          }
          
          /* Text alignment */
          .text-center {
            text-align: center;
          }
          
          .text-right {
            text-align: right;
          }
          
          /* Word break */
          .break-words {
            word-wrap: break-word;
            overflow-wrap: break-word;
          }
          
          /* White space */
          .whitespace-normal {
            white-space: normal;
          }
          
          /* Overflow */
          .overflow-visible {
            overflow: visible;
          }
          
          /* Display utilities */
          .block {
            display: block;
          }
          
          /* Borders */
          .border-t {
            border-top: 1px solid;
          }
          
          .border-gray-200 {
            border-color: #e5e7eb;
          }
          
          /* Padding */
          .pr-4 {
            padding-right: 1rem;
          }
          
          .pt-3 {
            padding-top: 0.75rem;
          }
          
          .p-8 {
            padding: 2rem;
          }
          
          /* Margin */
          .mt-4 {
            margin-top: 1rem;
          }
          
          .mb-6 {
            margin-bottom: 1.5rem;
          }
          
          .mr-3 {
            margin-right: 0.75rem;
          }
          
          /* UMD Brand Colors - WCAG compliant */
          .bg-\\[\\#e21833\\] {
            background-color: #e21833;
          }
          
          .bg-\\[\\#454545\\] {
            background-color: #454545;
          }
          
          .bg-\\[\\#7f7f7f\\] {
            background-color: #7f7f7f;
          }
          
          .bg-\\[\\#e6e6e6\\] {
            background-color: #e6e6e6;
          }
          
          /* Text colors for bars */
          .text-white {
            color: white;
          }
          
          .text-black {
            color: black;
          }
          
          /* Text size utility */
          .text-2xl {
            font-size: 1.5rem;
          }
          
          .text-3xl {
            font-size: 1.875rem;
          }
          
          /* Transitions - disable for print */
          .transition-all {
            transition: none;
          }
          
          .duration-300 {
            transition-duration: 0s;
          }
          
          .hover\\:opacity-80:hover {
            opacity: 1;
          }
          
          /* Chart container wrapper */
          .print-chart-container {
            width: 100%;
            overflow: visible;
            position: relative;
          }
          
          /* Ensure flex containers don't overflow */
          .flex-1 {
            flex: 1 1 0%;
            min-width: 0;
          }
          
          /* Flex-wrap utilities */
          .flex-wrap {
            flex-wrap: wrap;
          }
          
          /* Gap utilities for flex */
          .gap-1 {
            gap: 0.25rem;
          }
          
          /* Flex direction */
          .flex-col {
            flex-direction: column;
          }
          
          /* Additional flex utilities */
          .items-start {
            align-items: flex-start;
          }
          
          .items-end {
            align-items: flex-end;
          }
          
          /* Background colors */
          .bg-white {
            background: white;
          }
          
          /* Rounded corners */
          .rounded-lg {
            border-radius: 0.5rem;
          }
          
          .rounded-full {
            border-radius: 9999px;
          }
          
          /* Padding utilities */
          .px-2 {
            padding-left: 0.5rem;
            padding-right: 0.5rem;
          }
          
          .py-1 {
            padding-top: 0.25rem;
            padding-bottom: 0.25rem;
          }
          
          .px-3 {
            padding-left: 0.75rem;
            padding-right: 0.75rem;
          }
          
          .py-0 {
            padding-top: 0;
            padding-bottom: 0;
          }
          
          .py-0\\.5 {
            padding-top: 0.125rem;
            padding-bottom: 0.125rem;
          }
          
          .p-4 {
            padding: 1rem;
          }
          
          .p-6 {
            padding: 1.5rem;
          }
          
          /* Margin utilities */
          .mb-2 {
            margin-bottom: 0.5rem;
          }
          
          .mb-3 {
            margin-bottom: 0.75rem;
          }
          
          .mb-4 {
            margin-bottom: 1rem;
          }
          
          .mt-2 {
            margin-top: 0.5rem;
          }
          
          .mt-3 {
            margin-top: 0.75rem;
          }
          
          .ml-2 {
            margin-left: 0.5rem;
          }
          
          /* Border utilities */
          .border {
            border: 1px solid;
          }
          
          .border-gray-200 {
            border-color: #e5e7eb;
          }
          
          .border-gray-300 {
            border-color: #d1d5db;
          }
          
          /* Heatmap grid support (CLOHeatmap / PLOHeatmap) */
          .inline-grid {
            display: inline-grid;
          }

          .gap-px {
            gap: 1px;
          }

          .overflow-x-auto {
            overflow-x: auto;
          }

          .overflow-hidden {
            overflow: hidden;
          }

          .shrink-0 {
            flex-shrink: 0;
          }

          .leading-snug {
            line-height: 1.375;
          }

          .tracking-wider {
            letter-spacing: 0.05em;
          }

          .uppercase {
            text-transform: uppercase;
          }

          .truncate {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .select-none {
            -webkit-user-select: none;
            user-select: none;
          }

          .cursor-default {
            cursor: default;
          }

          .pointer-events-none {
            pointer-events: none;
          }

          .fixed {
            position: fixed;
          }

          .z-50 {
            z-index: 50;
          }

          .max-w-xs {
            max-width: 20rem;
          }

          .shadow-xl {
            box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04);
          }

          .gap-1\\.5 {
            gap: 0.375rem;
          }

          .bg-gray-200 {
            background-color: #e5e7eb;
          }

          .w-5 {
            width: 1.25rem;
          }

          .w-6 {
            width: 1.5rem;
          }

          .mt-0\\.5 {
            margin-top: 0.125rem;
          }

          .px-4 {
            padding-left: 1rem;
            padding-right: 1rem;
          }

          .py-3 {
            padding-top: 0.75rem;
            padding-bottom: 0.75rem;
          }

          .py-2 {
            padding-top: 0.5rem;
            padding-bottom: 0.5rem;
          }

          .text-gray-400 {
            color: #9ca3af;
          }

          .text-gray-500 {
            color: #6b7280;
          }

          .justify-center {
            justify-content: center;
          }

          .opacity-20 {
            opacity: 0.2;
          }

          /* SVG and Recharts support */
          svg {
            max-width: 100%;
            height: auto;
            overflow: visible;
          }
          
          .recharts-wrapper {
            width: 100% !important;
            height: auto !important;
          }
          
          .recharts-surface {
            overflow: visible !important;
          }
          
          /* Heatmap print wrapper — screen: no-op, print: scaled landscape */
          .heatmap-print-wrapper {
            width: 100%;
          }

          /* Print-specific styles */
          @media print {
            @page {
              margin: 0.5in;
              size: Letter portrait;
            }
            
            body {
              padding: 0;
              overflow: visible;
            }
            
            /* Ensure SVG and Recharts render in print */
            svg {
              max-width: 100% !important;
              height: auto !important;
              overflow: visible !important;
            }
            
            .recharts-wrapper,
            .recharts-surface,
            .recharts-layer {
              overflow: visible !important;
            }
            
            /* Force Recharts ResponsiveContainer to full width */
            .recharts-responsive-container {
              width: 100% !important;
              height: auto !important;
              min-height: 400px;
            }
            
            /* Global text normalization for print */
            * {
              white-space: normal !important;
              word-break: normal !important;
              overflow-wrap: break-word !important;
            }

            /* Hide fixed-position elements (tooltips) in print */
            .fixed {
              display: none !important;
            }

            /* Heatmap grid: preserve layout in print */
            .inline-grid {
              display: inline-grid !important;
            }

            .gap-px {
              gap: 1px !important;
            }

            .overflow-hidden {
              overflow: hidden !important;
            }

            /* Make the horizontal scroll wrapper fill available width */
            .heatmap-print-wrapper .overflow-x-auto {
              overflow: visible !important;
              width: 100%;
            }

            /* Ensure heatmap colors print correctly */
            .inline-grid > div,
            .heatmap-print-wrapper * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            /* Reset flex items to prevent width issues */
            .flex-1 {
              flex: 1 1 0% !important;
              min-width: 0 !important;
              max-width: none !important;
            }
            
            /* Ensure all text containers have proper width */
            h1, h2, h3, h4, h5, h6, p, span, div {
              max-width: 100% !important;
            }
            
            /* Page break controls */
            .bg-white,
            .shadow-lg,
            .rounded-xl {
              page-break-inside: avoid;
              break-inside: avoid;
            }
            
            h1, h2, h3, h4, h5, h6 {
              page-break-after: avoid;
              break-after: avoid;
              page-break-inside: avoid;
              break-inside: avoid;
            }
            
            /* Keep grid items together */
            .grid > * {
              page-break-inside: avoid;
              break-inside: avoid;
            }
            
            /* Learning Objectives specific print styles */
            .bg-gray-50 {
              page-break-inside: avoid;
              break-inside: avoid;
            }
            
            /* Ensure flex-wrap works properly in print */
            .flex-wrap {
              flex-wrap: wrap !important;
            }
            
            /* Force single column layout for grids in print for better readability */
            .grid-cols-1.md\\:grid-cols-2 {
              grid-template-columns: 1fr !important;
            }

            /* Safari ignores break-inside: avoid, so card grids that already
               render single-column in print are converted to block flow. As block
               elements (not grid items) each card can take a real forced page
               break, letting paginateForSafariPrint (accessiblePdfExport) move a
               card cleanly to the next page instead of using the drift-prone
               grid-margin fallback that splits cards mid-content. Heatmaps use
               .inline-grid and are unaffected. */
            .grid.grid-cols-1 {
              display: block !important;
            }
            .grid.grid-cols-1 > * {
              margin-top: 1rem !important;
            }
            .grid.grid-cols-1 > *:first-child {
              margin-top: 0 !important;
            }
            
            /* Fix Learning Objectives text wrapping - use block layout instead of flex */
            .bg-gray-50 > div > div.flex.items-start.justify-between {
              display: block !important;
            }
            
            .bg-gray-50 h3 {
              white-space: normal !important;
              word-break: normal !important;
              overflow-wrap: break-word !important;
              width: 100% !important;
              max-width: 100% !important;
              display: block !important;
              margin-bottom: 0.5rem !important;
            }
            
            /* Badge container below title in print */
            .bg-gray-50 .flex.flex-col.gap-2 {
              display: flex !important;
              flex-direction: row !important;
              flex-wrap: wrap !important;
              gap: 0.5rem !important;
              margin-bottom: 0.5rem !important;
            }
            
            /* Ensure tag containers don't overflow */
            .flex.flex-wrap.gap-1 {
              display: flex !important;
              flex-wrap: wrap !important;
              gap: 0.25rem !important;
              max-width: 100%;
            }
            
            /* Make sure tags don't break awkwardly */
            .flex-wrap > span {
              page-break-inside: avoid;
              break-inside: avoid;
              display: inline-block;
            }
            
            /* Keep chart sections together */
            .space-y-3 > * {
              page-break-inside: avoid;
              break-inside: avoid;
            }
            
            /* Avoid orphans and widows */
            p {
              orphans: 3;
              widows: 3;
            }
            
            /* Remove shadows for print */
            .shadow-lg {
              box-shadow: none;
              border: 1px solid #e5e7eb;
            }
            
            /* Ensure colors print correctly */
            * {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              color-adjust: exact;
            }
            
            /* Ensure flex layout works in print */
            .flex {
              display: flex !important;
            }
            
            .flex-1 {
              flex: 1 1 0% !important;
            }
            
            /* Ensure backgrounds print */
            .bg-\\[\\#e21833\\],
            .bg-\\[\\#454545\\],
            .bg-\\[\\#7f7f7f\\],
            .bg-\\[\\#e6e6e6\\] {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            
            /* Scale down large charts to fit page width */
            .print-chart-container {
              width: 100%;
              max-width: 100%;
              overflow: visible !important;
              page-break-inside: avoid;
              break-inside: avoid;
            }
            
            /* Scale the chart content to fit within printable area.
               Use zoom instead of transform: zoom shrinks the layout footprint so
               overflow-x-auto does not clip the content before painting (Safari fix). */
            .print-chart-container > .space-y-3 {
              width: fit-content;
              max-width: none;
            }

            /* For normal charts (<=20 problems), apply moderate scaling.
               Column budget: 192 + 20*35 + 38 = 930px; 930*0.70 = 651px < 720px page */
            .print-chart-container.print-scale-normal {
              font-size: 9pt;
            }

            .print-chart-container.print-scale-normal > .space-y-3 {
              zoom: 0.70;
              width: fit-content;
            }

            /* Reduce column widths for normal print */
            .print-chart-container.print-scale-normal .min-w-\\[60px\\] {
              min-width: 35px !important;
            }

            /* For charts with many problems (>20), scale to fit width.
               Column budget: 160 + 40*25 + 78 = 1238px; 1238*0.55 = 681px < 720px page */
            .print-chart-container.print-scale-large {
              font-size: 8pt;
            }

            .print-chart-container.print-scale-large > .space-y-3 {
              zoom: 0.55;
              width: fit-content;
            }

            /* For charts with very many problems (>40), scale more aggressively.
               Column budget: 128 + 60*22 + 118 = 1566px; 1566*0.45 = 705px < 720px page */
            .print-chart-container.print-scale-very-large {
              font-size: 7pt;
            }

            .print-chart-container.print-scale-very-large > .space-y-3 {
              zoom: 0.45;
              width: fit-content;
            }

            /* Reduce column widths further for print */
            .print-chart-container.print-scale-large .min-w-\\[45px\\],
            .print-chart-container.print-scale-large .min-w-\\[60px\\] {
              min-width: 25px !important;
            }

            .print-chart-container.print-scale-very-large .min-w-\\[35px\\],
            .print-chart-container.print-scale-very-large .min-w-\\[45px\\] {
              min-width: 22px !important;
            }

            /* Ensure parent container doesn't clip scaled content */
            .bg-white.p-8.rounded-xl:has(.print-chart-container) {
              overflow: visible !important;
              max-width: none !important;
            }

            /* Safari fix: overflow-x-auto clips chart layout before painting.
               zoom shrinks the layout box so this is belt-and-suspenders. */
            .overflow-x-auto:has(.print-chart-container) {
              overflow: visible !important;
              max-width: none !important;
            }
            
            /* Make sure flex items are visible */
            .print-chart-container .flex {
              overflow: visible !important;
              max-width: none !important;
            }
            
            .print-chart-container .flex-1 {
              overflow: visible !important;
              max-width: none !important;
            }
            
            /* Override any container constraints */
            html, body {
              overflow: visible !important;
            }
            
            .space-y-8, .space-y-6, .space-y-4, .space-y-3 {
              overflow: visible !important;
            }
          }
        </style>
      </head>
      <body>
        ${content}
        
        <script>
          // Auto-close window after printing
          window.onafterprint = function() {
            window.close();
          };
          
          // Fallback for browsers that don't support onafterprint
          if (window.matchMedia) {
            var mediaQueryList = window.matchMedia('print');
            mediaQueryList.addListener(function(mql) {
              if (!mql.matches) {
                window.close();
              }
            });
          }
          
          // Additional fallback: close if user navigates away or cancels
          window.onbeforeprint = function() {
            // Set a flag that print was started
            window.printStarted = true;
          };
        </script>
      </body>
    </html>
  `;
}
